import { Injectable } from '@angular/core';
import { Client, StompSubscription } from '@stomp/stompjs';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
// @ts-ignore
import SockJS from 'sockjs-client';
import { fetchAuthSession } from 'aws-amplify/auth';
import { environment } from '../environments/environment';
import { Mail } from '../models/mail.model';

@Injectable({
  providedIn: 'root',
})
export class WebsocketService {
  private client: Client | null = null;
  private isConnected = new BehaviorSubject<boolean>(false);
  private subscriptions: Map<string, StompSubscription> = new Map();
  /** Token usato dall'ultimo tentativo di connessione, rinfrescato da `beforeConnect`. */
  private currentToken: string | null = null;

  isConnected$ = this.isConnected.asObservable();

  private disconnected$ = new Subject<boolean>();
  disconnectedObservable$ = this.disconnected$.asObservable();

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      // onConnect/onStompError coprono solo il protocollo STOMP una volta stabilito il
      // trasporto. Un fallimento di trasporto (URL errato, backend giù, CORS, rete) non
      // passa da nessuno dei due: senza questi due callback la Promise non si sarebbe mai
      // risolta né rigettata, e il client STOMP avrebbe ritentato in silenzio all'infinito
      // (reconnectDelay di default) senza che nulla comparisse in console.
      let settled = false;

      this.client = new Client({
        // Il token viene risolto prima di OGNI tentativo, riconnessioni incluse: `beforeConnect`
        // è awaited dal client a ogni `_connect()`. Risolverlo una volta sola fuori di qui
        // significava che, se la prima risoluzione falliva o restituiva un token già scaduto,
        // tutti i retry successivi riusavano per sempre quel valore e la connessione non si
        // ristabiliva più senza un reload della pagina.
        beforeConnect: async () => {
          const token = await this.resolveToken();
          this.client!.connectHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        },
        // il token viene passato nell'header
        webSocketFactory: () => new SockJS(`${environment.apiUrl}/ws`, null, { withCredentials: false }) as any,
        onConnect: () => {
          console.log('WebSocket connected');
          this.isConnected.next(true);
          settled = true;
          resolve();
        },
        onStompError: (frame: any) => {
          console.error('STOMP error:', frame);
          this.isConnected.next(false);
          if (!settled) {
            settled = true;
            reject(frame);
          }
        },
        onWebSocketError: (event: any) => {
          console.error('WebSocket transport error:', event);
          this.isConnected.next(false);
          if (!settled) {
            settled = true;
            reject(event);
          }
        },
        onWebSocketClose: (event: any) => {
          console.warn('WebSocket transport closed:', event);
          this.isConnected.next(false);
          if (!settled) {
            settled = true;
            reject(event);
          }
        },
        onDisconnect: () => {
          console.log('WebSocket disconnected');
          this.isConnected.next(false);
        },
      });

      this.client.activate();
    });
  }

  /**
   * Non solleva mai: un errore qui (Amplify non ancora configurato, sessione non ancora
   * ripristinata dopo un reload) deve tradursi in un tentativo fallito e riprovato, non in
   * un `beforeConnect` che rigetta lasciando il client bloccato.
   */
  private async resolveToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString() ?? null;
      if (!token) {
        console.error('[WS] Nessun token di sessione disponibile: il backend rifiuterà la connessione');
      }
      return token;
    } catch (error) {
      console.error('[WS] Recupero del token di sessione fallito:', error);
      return null;
    }
  }

  disconnect(): Promise<void> {
    return new Promise(resolve => {
      if (this.client) {
        this.subscriptions.forEach(subscription => {
          subscription.unsubscribe();
        });
        this.subscriptions.clear();
        this.client.deactivate();
      }
      resolve();
    });
  }

  subscribe(destination: string): Observable<any> {
    // @ts-ignore
    return new Observable(observer => {
      if (!this.client) {
        observer.error('WebSocket not initialized');
        return;
      }

      if (!this.client.active) {
        observer.error('WebSocket not connected');
        return;
      }

      try {
        const subscription = this.client.subscribe(
          destination,
          message => {
            try {
              const data = JSON.parse(message.body);
              observer.next(data);
            } catch (error) {
              console.error(`[WS] JSON parsing error (${destination}):`, error, message.body);
            }
          },
          {
            ack: 'auto',
          },
        );

        this.subscriptions.set(destination, subscription);
        console.log(`[WS] Subscription created (${destination})`);

        return () => {
          console.log(`[WS] Deleting subscription (${destination})`);
          subscription.unsubscribe();
          this.subscriptions.delete(destination);
        };
      } catch (error) {
        console.error(`[WS] Subscription error (${destination}):`, error);
        observer.error(error);
      }
    });
  }

  /** @returns `true` se il frame è stato pubblicato, `false` se il client non era connesso. */
  send(destination: string, body: any): boolean {
    if (!this.client || !this.client.connected) {
      console.error(`[WS] Invio su ${destination} non riuscito: WebSocket non connesso`);
      return false;
    }
    this.client.publish({
      destination,
      body: JSON.stringify(body),
    });
    return true;
  }

  lockPec(mail: Mail) {
    const uuid = crypto.randomUUID();
    const messageRequest = {
      documentId: mail.id,
      idRiferimento: mail.idRiferimento,
      lock: true,
      uuid: uuid,
    };
    // Il lock viene persistito solo se la richiesta è davvero partita: salvarlo comunque
    // lasciava in localStorage un lock fantasma che il dettaglio considerava valido (e che
    // veniva poi inviato come uuidLock su GET /messages/{idRiferimento}).
    if (this.send('/app/messages/lock', messageRequest)) {
      localStorage.setItem('message-lock', JSON.stringify(messageRequest));
    }
  }

  unlockPec(mail: Mail) {
    const lockState = localStorage.getItem('message-lock');
    if (lockState != null) {
      const state = JSON.parse(lockState);
      // User has lock state info
      // Lock state uuid is OK
      this.send('/app/messages/lock', {
        documentId: state.documentId,
        uuid: state.uuid,
        idRiferimento: mail.idRiferimento,
        lock: false,
      });
      localStorage.removeItem('message-lock');
    }
  }
}
